import React from "react";
import { useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import { Share2Icon, StarIcon } from "lucide-react";
import { GetBattleByIdApiResponse as Battle } from "@dripit/types";
import dayjs from "dayjs";

import { BattleStatus } from "@/types";
import { cn } from "@/helpers/tailwind";
import { Button } from "@/components/ui";
import { ShareBattleDialog } from "@/pages/battles/components";
import { starBattle } from "@/api/battleQueries";
import { getBattleStatus } from "@/helpers/battleHelpers";
import { NewBattleLabel } from "./NewBattleLabel";

interface BattleHeaderActionsProps {
  isMobileTopNav?: boolean;
  battle?: Battle;
  loginBtn?: React.ReactNode;
  refetchBattle?: () => void;
}

export const BattleHeaderActions: React.FC<BattleHeaderActionsProps> = ({
  isMobileTopNav = false,
  battle,
  loginBtn = null,
  refetchBattle,
}) => {
  const isLoggedIn = useIsLoggedIn();
  const mutation = starBattle(battle?.id?.toString() ?? "");
  const battleStatus = React.useMemo(() => getBattleStatus(battle), [battle]);

  const content = {
    loginBtn,
    shareBtn: (
      <ShareBattleDialog
        battle={battle}
        triggerButton={
          <Button
            className={cn(
              "size-12 border border-slate-300 bg-transparent text-slate-300",
              "rounded-full hover:bg-transparent hover:text-slate-300",
              { "border-none": isMobileTopNav }
            )}>
            <Share2Icon className={cn({ "!size-6": isMobileTopNav })} />
          </Button>
        }
      />
    ),
    starBtn: (
      <Button
        className={cn(
          "size-12 rounded-full border",
          "transition-all duration-300 ease-in-out",
          {
            "size-10 border-none": isMobileTopNav,
            "border-yellow-300 bg-transparent text-yellow-300 hover:border-slate-300 hover:bg-transparent hover:text-yellow-300":
              battle?.starred,
            "border-slate-300 bg-transparent text-slate-300 hover:border-yellow-300 hover:bg-transparent hover:text-yellow-300":
              !battle?.starred,
          }
        )}
        disabled={mutation.isPending}
        onClick={async () => {
          await mutation.mutateAsync(
            { isStarred: !battle?.starred },
            {
              onSuccess: async () => {
                refetchBattle?.();
              },
            }
          );
        }}>
        <StarIcon
          className={cn("transition-all duration-300 ease-in-out", {
            "fill-transparent": !battle?.starred,
            "fill-yellow-300": battle?.starred,
            "!size-6": isMobileTopNav,
          })}
          strokeWidth={1.5}
        />
      </Button>
    ),
    newLabel: battleStatus === BattleStatus.Open &&
      battle?.created_at &&
      dayjs().diff(dayjs(battle.created_at * 1000), "day") <= 2 && (
        <>
          <NewBattleLabel createdAt={battle?.created_at} />
          {!isMobileTopNav && <div className="mx-4 h-8 w-px bg-slate-700" />}
        </>
      ),
  };

  return (
    <div className="flex grow items-center justify-end gap-3">
      {content.newLabel}
      {content.shareBtn}
      {isLoggedIn ? content.starBtn : content.loginBtn}
    </div>
  );
};
